import { useCallback, useMemo, useState, type ReactNode } from "react";
export { SchoolReports, type SchoolReportsProps } from "./SchoolReportsScreen";
import {
  ActivityLineChart,
  AlertIcon,
  ChartPanel,
  ChildrenIcon,
  DataSurface,
  Field,
  InfoIcon,
  Note,
  PlayIcon,
  RankedBars,
  ReportsIcon,
  SchoolIcon,
  SectionHead,
  StatCard,
  StatGrid,
  TrendUpIcon,
} from "../../../shared/components";
import { useResource, type Resource } from "../../../shared/hooks/useResource";
/* 🔴 ONE BAR IN THE TIER, NOT TWO. This screen had grown its own `ShareBar`
   the same week E5 grew `ShareBars` — the identical fact drawn twice, a nav
   item apart, which is the split `DashUI`'s tile header describes and the one
   this folder keeps having to undo. */
import { ShareBars } from "./SchoolCharts";
import {
  getSchoolActivity,
  getSchoolOverview,
  type SchoolActivity,
  type SchoolOverview,
} from "../../../shared/api/school";

import type { StaffAuth } from "../../../shared/api/teaching";
import type { Lang } from "./copy";
import {
  ADMIN_COPY,
  describeAdminError,
  noteText,
} from "./adminCopy";
import {
  schoolNotes,
} from "./schoolDashboardModel";
/* 🔴 EVERY DECISION THE DATE FILTER MAKES, PURE AND TESTED, NONE OF IT IN THIS
   FILE. `dashboardRange.ts` carries why it mirrors the server's own parser. */
import {
  DEFAULT_RANGE_DAYS,
  isCompleteRange,
  earliestFrom,
  presetRange,
  settleRange,
  todayUtc,
  type DateRange,
} from "./dashboardRange";
import {
  GameUsageCard,
} from "./SchoolPlayfulUI";

/**
 * E1, E5 and E6.
 *
 * 🔴 EVERY SCREEN BELOW ISSUES ITS OWN REAL REQUEST TO ITS OWN REAL PATH. None
 * takes its data as a prop and there is no fixture file in this directory —
 * §8.4's discipline, whose TODO row reads *"the predecessor rendered 8 hardcoded
 * children while working endpoints sat uncalled"*. A component that takes `rows`
 * with sample data beside it is the same failure one level up.
 *
 * 🔴 EVERY BRANCH WORTH TESTING LIVES IN `schoolDashboardModel.ts`. The repo has
 * no DOM renderer (OD-60) and may not gain one, so a decision made inside a
 * component is a decision no test can reach.
 *
 * ✅ S1 — THE YEAR-END ROLLOVER — IS NO LONGER ONE OF THEM. The screen and
 * `rolloverModel.ts` are gone; `RolloverService` and its endpoints are untouched,
 * so a school that has already run one keeps the record.
 */

interface Base {
  lang?: Lang;
  auth?: StaffAuth;
}

/** The four states, wired to `DataSurface`'s contract in one place. */
function Surface<T>({
  resource,
  lang,
  children,
  emptyMessage,
  skeletonRows = 4,
}: {
  resource: Resource<T>;
  lang: Lang;
  children: (data: T) => ReactNode;
  emptyMessage?: ReactNode;
  skeletonRows?: number;
}) {
  const t = ADMIN_COPY[lang];
  return (
    <DataSurface
      state={resource.state}
      skeletonRows={skeletonRows}
      emptyMessage={emptyMessage ?? t.noteNoChildren}
      errorMessage={describeAdminError(resource.error, lang)}
      /* 🔴 "When it last worked" — DataSurface calls this the clause that gets
         dropped, and the one that tells an admin whether to panic. */
      errorLastWorked={
        resource.lastLoadedAt
          ? t.lastWorked(resource.lastLoadedAt.toLocaleTimeString())
          : t.neverWorked
      }
      errorRetryLabel={t.retry}
      onRetry={resource.retry}
    >
      {resource.data === null ? null : children(resource.data)}
    </DataSurface>
  );
}

/* ─────────────────────────────────────────────────────────────── E1 ── */

export interface SchoolDashboardProps extends Base {
  /** S3 lives at its own route; E1 links to it rather than restating it. */
  onOpenClassConsent: (classId: string) => void;
  /**
   * Every destination this screen can send a reader to, handed in rather than
   * routed from here. The stat strip is the only thing that uses it now — the
   * quick-action bar shared it until the bar was removed, and the argument that
   * earned the prop is the reason the tiles kept it.
   *
   * 🔴 A DESTINATION HANDED IN CANNOT BE AN INERT CONTROL. The bar this prop was
   * written for held six buttons with no `onClick` between them: a control that
   * looks live and does nothing is worse than an absent one, because it costs
   * the reader a click to learn it is dead and teaches them not to trust the
   * next button. Passing the destinations in makes that unwriteable rather than
   * merely discouraged — a caller that cannot navigate hands over nothing, and
   * `StatCard` draws no chevron, no `<button>` and no focus ring without an
   * `onClick`. A tile with nowhere to go promises nothing.
   *
   * 🔴 EVERY FIGURE ON THE STRIP IS THE HEADLINE OF A SCREEN THAT EXISTS —
   * "Children" is E7's roster, "Sessions this week" is E5's sittings — and a
   * reader who wants the names behind a number should not have to go find the
   * sidebar item that repeats the word.
   *
   * ⚠️ `reports` IS ON THIS UNION BECAUSE TWO TILES LAND ON IT. "Played" and
   * "Sessions this week" both open E5, which prints the pair side by side; that
   * is one destination for two figures, not a duplicate.
   *
   * ⚠️ OPTIONAL, AND ABSENT MEANS ABSENT. `AppRouter` is the only caller today
   * and it always passes one, but a viewer of somebody else's school must not be
   * handed a tile that navigates THEIR chrome into a school that is not theirs —
   * so the absence has to stay expressible, and `StatCard` has to keep reading
   * it as "draw no door".
   */
  onNavigate?: (
    to: "classes" | "teachers" | "students" | "reports",
  ) => void;
  /**
   * Open the play log for one game.
   *
   * ⚠️ OPTIONAL, AND ABSENT MEANS THE CARD IS NOT A BUTTON. The operator
   * tier renders this same dashboard through `SchoolDashboardView` to look at
   * somebody else's school; a card that navigated their chrome into a school
   * route is the same mistake `onNavigate` already refuses to make. Without the
   * prop the cards stay the plain figures they were.
   */
  onOpenGame?: (game: string) => void;
}

/*
 * ✅ THE ONE-CLICK WINDOWS (7 / 30 / 90) NO LONGER APPEAR ON E1, and the block
 * that documented them went with the constant. The filter is the two dates now
 * — *"filter in right side, only the dates"* — and the span those tabs selected
 * is already printed in words beside them: the headline reads "… — 30 days".
 *
 * ⚠️ NOTHING PURE WAS DELETED. `RANGE_PRESETS` and `matchedPreset` are still in
 * `dashboardRange.ts` under test, and `presetRange` still builds this page's
 * opening range. Restoring the tabs is re-adding one element to `RangeFilter`.
 */

/**
 * A `YYYY-MM-DD` from the API, written the way a person reads a date.
 *
 * ⚠️ PARSED AS UTC AND FORMATTED AS UTC. `new Date('2026-03-05')` is midnight
 * UTC, and formatting it in a timezone behind Greenwich prints the 4th — an
 * off-by-one that would appear only for readers west of London and only some of
 * the time, which is the worst kind of date bug to be told about.
 */
function readableDay(day: string): string {
  const at = new Date(`${day}T00:00:00.000Z`);
  if (Number.isNaN(at.getTime())) return day;
  return at.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

/**
 * E1's date filter — two date inputs, and what the server settled on.
 *
 * 🔴 IT SITS OUTSIDE THE PAGE'S `Surface`, WHICH IS THE ONE PLACEMENT DECISION
 * WORTH ARGUING. Inside, it would be drawn only in the `ready` state — so a
 * reader whose range produced an error, or whose slow request left them on a
 * skeleton, would have no control to change it with and no way out but a
 * reload. A filter that disappears exactly when the thing it filters went wrong
 * is a trap.
 *
 * 🔴 EVERY BRANCH IT HAS LIVES IN `dashboardRange.ts`. Which tab is lit, what a
 * backwards pair means, when a range is complete enough to fetch: all pure,
 * all tested, none of it decided in here — this folder's header on why.
 */
function RangeFilter({
  draft,
  onDraft,
  lang,
  busy,
  measured,
}: {
  draft: DateRange;
  onDraft: (next: DateRange) => void;
  lang: Lang;
  busy: boolean;
  /**
   * The range the server says it measured, once a read has landed.
   *
   * ⚠️ THE SUMMARY LINE IS DRAWN FROM THIS AND NOT FROM `draft`. The server
   * resolves rather than refuses, so the two differ whenever a range was
   * swapped, clamped or capped — and printing the reader's dates over numbers
   * measured across different ones is the specific dishonesty `windowFrom`
   * exists to prevent.
   */
  measured: { from: string; to: string; days: number } | null;
}) {
  const t = ADMIN_COPY[lang];
  const today = todayUtc();
  /* Asked for one thing, measured another — see `rangeAdjusted`. */
  const adjusted =
    measured !== null &&
    isCompleteRange(draft) &&
    (measured.from !== draft.from || measured.to !== draft.to);

  return (
    <div className="rounded-3xl border border-line bg-surface p-4 shadow-card sm:p-5">
      <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-4">
        {/*
          🔴 THE SUMMARY AND THE DATES SHARE ONE ROW AGAIN — AND THE MEASUREMENT
          THAT ONCE FORBADE IT NO LONGER HOLDS.
          The note this replaces was right about its own layout: three controls
          wanted ~710px (preset tabs ~205, plus two `Field`s capped at 240 each),
          a `rangeScopeNote` of ~700px wanted the same line, and 1435px of card
          interior does not exist at a 1536px viewport once the sidebar and
          gutters are gone. So the summary was given `basis-full` and the
          controls got a row to themselves.
          🔴 REMOVING THE PRESET TABS AND CAPPING THE DATES AT 168px CHANGES THE
          ARITHMETIC: the cluster is now ~348px rather than ~710. That fits
          beside a summary which is free to wrap onto two lines, so the filter
          sits where it was asked to sit — on the right — and the card loses a
          whole row of height.
          ⚠️ `basis-full md:basis-0` IS THE PHONE ESCAPE. Below `md` the summary
          still takes its own row and the dates wrap under it, because at 390px
          nothing shares a line and `responsive.spec.ts` fails the build for
          anything past the viewport.
        */}
        <div className="min-w-0 flex-1 basis-full md:basis-0">
          {/* 🔴 THE MEASURED RANGE, IN WORDS, AND IT IS THE HEADLINE RATHER THAN
              a footnote: it is the sentence every figure below is scoped by. */}
          <p className="font-dash text-dash-sm font-extrabold text-ink">
            {measured === null
              ? t.dash
              : t.rangeShowing(
                  readableDay(measured.from),
                  readableDay(measured.to),
                  measured.days,
                )}
          </p>
          <p className="mt-0.5 font-dash text-dash-2xs font-bold text-muted">
            {t.rangeScopeNote}
          </p>
        </div>

        {/*
          🔴 THE FILTER, ON THE RIGHT, AND NOTHING IN IT BUT THE TWO DATES.
          The 7/30/90 preset tabs were removed from this bar rather than moved:
          asked for *"filter in right side, only the dates"*, and the span they
          selected is already stated in words one line to the left — the headline
          reads "… — 30 days". A control whose only output is a number already on
          screen is the weakest thing competing for that space.
          ⚠️ THE PRESETS ARE GONE FROM THE BAR, NOT FROM THE CODEBASE.
          `RANGE_PRESETS` and `matchedPreset` stay in `dashboardRange.ts` with
          their tests — they are pure and cheap, and putting the tabs back is
          re-adding one element here rather than rebuilding the logic.
          ⚠️ EACH FIELD IS CAPPED AT 168px BY ITS WRAPPER, NOT BY `Field`.
          `Field`'s own `sm` width is `max-w-[15rem]` — a 240px cap tuned for
          two-word selects in a toolbar — and a native date input draws in about
          150. Two of them at 240 spend ~140px on nothing, which is exactly the
          budget this row does not have. The cap lives out here so `Field` keeps
          meaning the same thing everywhere else it is used.
          ⚠️ `flex-1` BELOW `sm` SO THE PAIR SPLITS A PHONE EVENLY rather than
          leaving one 168px box beside a gap.
        */}
        <div
          /* ⚠️ THE BUSY CUE MOVED HERE WHEN THE PRESET TABS LEFT. `RangeTabs`
             dimmed itself while a read was in flight, and deleting it would have
             taken the bar's only "this is reloading" signal with it. Same 60%,
             same easing, now on the controls that remain. */
          className={`flex w-full shrink-0 items-end gap-3 transition-opacity duration-150 ease-dash md:w-auto ${
            busy ? "opacity-60" : ""
          }`}
        >
          <div className="min-w-0 flex-1 sm:w-[10.5rem] sm:flex-none">
          <Field label={t.rangeFrom} htmlFor="school-range-from">
            <input
              id="school-range-from"
              type="date"
              value={draft.from}
              /* No future dates, and no start after the end already chosen —
                 the browser's own picker enforces what `settleRange` would
                 otherwise have to undo after the fact. */
              max={draft.to !== "" && draft.to < today ? draft.to : today}
              /* 🔴 THE SPAN CEILING, MADE VISIBLE IN THE PICKER RATHER THAN
                 APPLIED SILENTLY AFTER THE FACT. It is a real limit — the
                 activity read is unpaginated, see `MAX_RANGE_DAYS` — and a
                 greyed-out day in the calendar explains it better than a range
                 that quietly snaps back once the answer arrives. */
              min={earliestFrom(draft.to)}
              onChange={(e) => {
                onDraft({ ...draft, from: e.target.value });
              }}
              className={DATE_INPUT}
            />
          </Field>
          </div>
          <div className="min-w-0 flex-1 sm:w-[10.5rem] sm:flex-none">
          <Field label={t.rangeTo} htmlFor="school-range-to">
            <input
              id="school-range-to"
              type="date"
              value={draft.to}
              min={draft.from === "" ? undefined : draft.from}
              max={today}
              onChange={(e) => {
                onDraft({ ...draft, to: e.target.value });
              }}
              className={DATE_INPUT}
            />
          </Field>
          </div>
        </div>
      </div>

      {adjusted && (
        <div className="mt-3">
          <Note tone="neutral" icon={<InfoIcon size={16} />}>
            {t.rangeAdjusted}
          </Note>
        </div>
      )}
    </div>
  );
}

/* Matches `Select`'s shell so the three controls read as one row of inputs. */
const DATE_INPUT =
  "h-10 cursor-pointer rounded-xl border border-line bg-canvas px-3 font-dash text-dash-xs font-bold text-ink outline-none transition-colors hover:border-line-strong focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-primary/20";

export function SchoolDashboard({
  lang = "en",
  auth,
  onOpenClassConsent,
  onNavigate,
  onOpenGame,
}: SchoolDashboardProps) {
  const t = ADMIN_COPY[lang];
  /* ✅ NO `sort` STATE. It drove the removed roster's Order dropdown; `ranked`
     below sorts quietest-first unconditionally, which is the only order that
     answers the question the panel asks. */

  /*
   * 🔴 THE PAGE'S RANGE, HELD IN TWO PIECES, AND THE SPLIT IS WHAT MAKES A DATE
   * INPUT USABLE.
   *
   * `draft` is what the two boxes show; `range` is what the requests are keyed
   * on. A single piece of state would put a fetch behind every keystroke —
   * `<input type="date">` fires `onChange` as a picker is scrubbed and as a
   * field is cleared — so the page would flash through a year of half-typed
   * ranges on the way to the one that was meant. `range` only advances when
   * `isCompleteRange` says both ends are real days.
   *
   * ⚠️ `settleRange` RUNS ON THE WAY IN, NOT ON THE WAY BACK. A backwards pair
   * swaps in the boxes as it is typed rather than after the server has answered
   * about a different period than the one on screen. The server settles it
   * again anyway — it is the authority and it must not trust a client — and
   * `RangeFilter` says so out loud on the rare occasion the two differ.
   */
  const [range, setRange] = useState<DateRange>(() =>
    presetRange(DEFAULT_RANGE_DAYS)
  );
  const [draft, setDraft] = useState<DateRange>(range);

  const applyDraft = useCallback((next: DateRange) => {
    const settled = settleRange(next);
    setDraft(settled);
    /* Half-typed is not an error and not a request — it is just not ready. */
    if (isCompleteRange(settled)) setRange(settled);
  }, []);

  /*
   * ✅ `applyPreset` IS GONE WITH THE TABS THAT CALLED IT. It set both pieces of
   * state at once — the only caller was `RangeTabs`, and the date inputs go
   * through `onDraft` → `settleRange` instead, which is the path that has always
   * carried the swap-and-clamp rules.
   *
   * ⚠️ `presetRange` STAYS IMPORTED AND USED: it still builds this page's OPENING
   * range (`DEFAULT_RANGE_DAYS`), which is what a reader sees before they touch
   * anything. Deleting the shortcut did not delete the default.
   */

  /*
   * ⚠️ THE RANGE IS IN THE KEY, WHICH IS THE ENTIRE MECHANISM. `useResource`
   * refetches when a key changes, so every figure this payload carries follows
   * the filter without a single manual invalidation — the tiles, the share
   * bars, the quietest-classes ranking and the class table all read from here.
   */
  const resource = useResource<SchoolOverview>(
    (signal) => getSchoolOverview({ ...auth, signal }, range),
    ["school-overview", range.from, range.to],
    { isEmpty: (o) => o.classes.length === 0 }
  );

  /*
   * 🔴 THE TREND IS ITS OWN REQUEST.
   *
   * `getSchoolOverview` answers five counters and the class table, and it is
   * what a head teacher looks at first. A ninety-day series is a heavier read;
   * folding it into the overview would make the tiles wait on it. It is the
   * split the teacher tier's `ClassActivityPanel` already makes, for the same
   * reason and with the
   * same consequence: each panel below owns its own four states, so a slow or
   * refused read blanks one panel rather than the page.
   *
   * ⚠️ IT TAKES THE SAME `range` THE OVERVIEW DOES, and both routes resolve it
   * through the one server-side parser. A head teacher reads "19 sittings" off
   * a tile and then adds up the chart directly beneath it; two windows that
   * could drift apart by a day would make that sum wrong for reasons nothing on
   * the screen could explain.
   *
   * ⚠️ THE RANGE IS IN THE KEY, so changing the dates refetches. That is the
   * whole difference between this control and the parent tier's period
   * `<select>`, which stored its value in state and never read it again.
   */
  const activity = useResource<SchoolActivity>(
    (signal) => getSchoolActivity(range, { ...auth, signal }),
    ["school-activity", range.from, range.to]
  );

  /*
   * 🔴 THE FOUR BUSIEST CLASSES, FROM THE SAME ARRAY THE TABLE BELOW RENDERS.
   *
   * ⚠️ A CLASS WITH NO CHILDREN IS EXCLUDED RATHER THAN RANKED ZERO. Its share
   * is 0/0, which is not a low score — it is a class nobody has been put in yet,
   * and a "busiest" list that opens with four empty classes has answered a
   * different question. Archived classes go for the same reason: they are not
   * quiet, they are closed.
   *
   * ⚠️ TIES BREAK ON THE COUNT, THEN ON THE NAME. Two classes at 100% are not
   * equally busy if one has twenty children and the other has two, and a
   * name-stable last resort stops the list reshuffling between renders.
   */
  /*
   * ✅ `.slice(0, 4)` IS GONE, AND `RankedBars` OWNS THE CUT-OFF NOW.
   *
   * The old list showed four and said nothing about the rest, which on an
   * eighteen-class school reads as "these are the classes" — the silent
   * truncation `DashCharts`' own header warns about. `RankedBars` draws five and
   * puts the remainder behind a control that names how many it is holding.
   *
   * ⚠️ THE ORDER IS INVERTED FROM WHAT IT WAS, AND THAT IS THE POINT. It was
   * "busiest first", which is a list of classes that need nothing. A head
   * teacher opens this page to find the quiet ones — the same argument the class
   * table below already makes by defaulting its sort to `quietest`. Sorting the
   * bars the other way made the two halves of one screen disagree about what
   * was worth looking at.
   */
  const ranked = useMemo(
    () =>
      (resource.data?.classes ?? [])
        .filter((c) => c.archivedAt === null && c.childCount > 0)
        .sort(
          (a, b) =>
            a.activeThisWeek / a.childCount - b.activeThisWeek / b.childCount ||
            a.activeThisWeek - b.activeThisWeek ||
            a.name.localeCompare(b.name)
        ),
    [resource.data]
  );

  return (
    <section className="font-dash text-dash-sm text-ink space-y-6 pb-8">
      {/*
        🔴 ABOVE THE `Surface`, NOT INSIDE IT — see `RangeFilter`'s own note.
        A control that vanishes in the error state is a control a reader cannot
        use to escape the error, and a bad range is one of the ways to get one.

        ⚠️ `measured` COMES FROM THE PAYLOAD, NOT FROM `range`. The server
        resolves rather than refuses, so the summary line has to state what was
        actually counted; while the first read is in flight there is nothing
        honest to say and the line draws an em dash.
      */}
      <RangeFilter
        draft={draft}
        onDraft={applyDraft}

        lang={lang}
        busy={resource.state === "loading" || activity.state === "loading"}
        measured={
          resource.data === null
            ? null
            : {
                from: resource.data.windowFrom,
                to: resource.data.windowTo,
                days: resource.data.windowDays,
              }
        }
      />

      <Surface resource={resource} lang={lang} skeletonRows={6}>
        {(o) => (
          <>
            {/*
              🔴 EVERY FIGURE HERE COMES OFF THE PAYLOAD THE SCREEN ALREADY
              FETCHED, AND THE ONES THAT COULD NOT ARE GONE.

              What was here: five tiles reading `{o.childCount || 348}`,
              `{o.teacherCount || 28}`, `{o.classCount || 16}`,
              `{o.skills?.length || 42}` and `{o.sessionsThisWeek || 78}` — real
              values with an invented number behind each `||`, so a school with
              no children on it read as a school of 348, and only a school with
              nothing at all ever saw the fiction. Under them sat "↑ 12 this
              month" on every card, a Sessions-vs-Active-Students line chart
              whose two curves were hand-drawn bezier paths, a donut split
              28/46/18/8 into bands the server has never computed, four invented
              assignments due in May, four invented "top performing" classes and
              four invented activity entries.

              ⚠️ TWO OF THOSE TILES WERE ALSO MISLABELLED. "Active Games" counted
              `o.skills.length` — the rows in the games table, not games being
              played — and "Assignments" showed `o.sessionsThisWeek`, which is
              sittings, a different thing from an assignment this product does
              not have. A label over the wrong number outlives an invented
              number, because the number itself is real.
            */}
            {/*
              🔴 FIVE COLUMNS OF `xs`, FOR THE REASON THE TEACHER OVERVIEW HAS
              SIX: A STRIP READ LEFT TO RIGHT IN ONE PASS, NOT A ROW AND A STUB.

              The default four-column grid left the last tiles floating under the
              left half — the eye reads orphans as a different kind of thing, and
              they sat above the fold where the class table below should be. The
              count follows the tiles: the strip carried six while Consent was
              one of them, and five now that it is not, because five tiles in a
              six-column grid is the same fault mirrored — a hole where the eye
              expects a tile.

              ⚠️ NOTHING ELSE IS DROPPED OR ABBREVIATED — same five remaining
              measures, same tones (including the one that goes `coral` on a
              finding), same captions, same fractions with their denominators
              printed. Only the box is smaller; `StatCard`'s `xs` note is why the
              labels stop wrapping when the column narrows.
            */}
            {/*
              🔴 EVERY TILE ON THIS STRIP OPENS SOMETHING, AND `onClick` IS
              PASSED CONDITIONALLY RATHER THAN GUARDED INSIDE THE HANDLER.

              `onNavigate && (() => …)` resolves to `undefined` for a caller
              that cannot navigate, and `StatCard` keys its whole affordance off
              that: no chevron, no `<button>`, no cursor change, no focus ring —
              a plain `<div>`, exactly as the strip read before. A handler that
              existed and quietly did nothing when the prop was absent would put
              a caret on the operator tier's copy of this screen and open
              nothing, which is the dead control the quick-action bar's note
              spends a paragraph on.

              ⚠️ THE FIGURES, THE TONES, THE CAPTIONS AND THE SIZE ARE ALL
              UNTOUCHED. `xs` still keeps the strip on one line, the
              finding-driven `coral` still follows its finding, and the fractions
              still print their denominators. The only thing added is a door.
            */}
            <StatGrid columns={5}>
              {/* Children → E7, the roster this number counts. */}
              <StatCard
                label={t.children}
                value={o.childCount}
                caption={
                  o.childrenWithNoClass > 0
                    ? t.childrenNotInAClass(o.childrenWithNoClass)
                    : undefined
                }
                tone={o.childrenWithNoClass > 0 ? "coral" : "indigo"}
                icon={<ChildrenIcon size={16} />}
                size="xs"
                onClick={
                  onNavigate &&
                  (() => {
                    onNavigate("students");
                  })
                }
              />
              {/* Teachers → E3, where one is invited and assigned. */}
              <StatCard
                label={t.teachers}
                value={o.teacherCount}
                tone="leaf"
                icon={<SchoolIcon size={16} />}
                size="xs"
                onClick={
                  onNavigate &&
                  (() => {
                    onNavigate("teachers");
                  })
                }
              />
              {/*
                Classes → E4, NOT the roster below. The tile counts classes that
                exist, and E4 is the screen where one is created, given a
                teacher, subjects and a week. The roster below answers a
                different question — which of them are quiet — and nothing on
                this strip goes there.
              */}
              <StatCard
                label={t.classes}
                value={o.classCount}
                caption={
                  o.archivedClassCount > 0
                    ? t.archivedClasses(o.archivedClassCount)
                    : undefined
                }
                tone="teal"
                icon={<ReportsIcon size={16} />}
                size="xs"
                onClick={
                  onNavigate &&
                  (() => {
                    onNavigate("classes");
                  })
                }
              />
              {/*
                🔴 A FRACTION, PRE-PAIRED, NEVER A PERCENTAGE — OD-39/OD-72, and
                `StatCard` takes a `ReactNode` value for exactly this. "23 / 40"
                is a fact a head teacher acts on; "58%" is a grade whose meaning
                depends entirely on what it was divided by.
              */}
              {/*
                Played → E5. Its own strip prints "Children who played" over the
                same window, and under it sit the classes that need a look and
                the per-skill breakdown — which is the next question anybody
                reading "1 / 33" has.
              */}
              <StatCard
                label={t.played}
                value={`${String(o.activeThisWeek)} / ${String(o.childCount)}`}
                caption={t.overWindow(o.windowDays)}
                tone="primary"
                icon={<PlayIcon size={16} />}
                size="xs"
                onClick={
                  onNavigate &&
                  (() => {
                    onNavigate("reports");
                  })
                }
              />
              {/*
                Sessions → E5 as well, and two tiles sharing a destination is
                correct rather than a duplicate. E5 prints both figures side by
                side ("Children who played" and "Sittings") because the pair is
                the finding: 19 sittings from 1 child is one school and 19 from
                19 children is another, and neither tile can say which alone.
              */}
              <StatCard
                label={t.sessionsThisWeek}
                value={o.sessionsThisWeek}
                caption={t.overWindow(o.windowDays)}
                tone="sunshine"
                icon={<TrendUpIcon size={16} />}
                size="xs"
                onClick={
                  onNavigate &&
                  (() => {
                    onNavigate("reports");
                  })
                }
              />
              {/*
                🔴 THE SIXTH TILE WAS CONSENT, AND IT IS GONE FROM THIS STRIP.
                `GET /school/overview` never carried the figure — its own
                description says consent is not restated there because *"a bare
                number would lose the sentence"* — and the tile that answered
                that objection from `GET /school/consent` has been taken off the
                school overview. Nothing here reads that route any more, and
                neither does the warning `Note` that used to print the sentence
                under this grid.

                ⚠️ CONSENT IS STILL ON THIS PAGE, ONE SCOPE DOWN. Every bar in
                "Classes that need a look" opens that class's consent —
                `RankedBars`' `onOpen` calls `onOpenClassConsent` — and the
                per-class `ConsentDashboard` is where the pending, withdrawn and
                lapsed families are broken out, which is the screen an admin
                chasing them actually works from. Only the whole-school counter
                is removed, not the door.

                (This note used to point at the classes roster's "Open consent"
                column instead. That roster has since been removed as the third
                class list on one page — see its own note below — and the bars
                above were always the same array with the same action on it.)
              */}
            </StatGrid>

            {/*
              🔴 THE HERO CHART, AND THE QUESTION E1 COULD NOT PREVIOUSLY ANSWER.
              Five counters over a fixed window cannot tell a head teacher
              whether a school has never started or stopped a fortnight ago, and
              those need opposite conversations. This is the same drawing the
              teacher's D4 uses, one scope up — literally the same component, so
              a school curve and a class curve cannot disagree about what a quiet
              week looks like.

              ⚠️ THE HINT IS DRAWN FROM `activity.data.windowDays`, NOT FROM THE
              RANGE STATE. The server resolves the request; a panel that
              labelled itself from what it ASKED for would mislabel itself on
              every occasion the two differ.

              ✅ THE WINDOW TABS ARE NO LONGER IN THIS HEADER. They used to be
              the panel's `action`, which was correct while they bounded this
              chart and nothing else — a control belongs beside the thing it
              changes. They now bound the whole page, so they live in
              `RangeFilter` at the top of it; leaving a copy here would be a
              second control for one piece of state, and the reader would have
              to discover that the two agree.

              ⚠️ IT KEEPS ITS OWN `DataSurface`. A refused or slow series must
              blank this panel and nothing else — the tiles above it and the
              table below it are different reads.
            */}
            <ChartPanel
              title={t.trendTitle}
              hint={t.trendHint(activity.data?.windowDays ?? o.windowDays)}
            >
              <DataSurface
                state={activity.state}
                skeletonRows={3}
                errorMessage={describeAdminError(activity.error, lang)}
                errorRetryLabel={t.retry}
                onRetry={activity.retry}
              >
                {activity.data === null ? null : (
                  <ActivityLineChart
                    points={activity.data.points}
                    height={230}
                    copy={{
                      sessions: t.trendSessions,
                      children: t.trendChildren,
                      nothingYet: t.trendNothing,
                    }}
                  />
                )}
              </DataSurface>
            </ChartPanel>

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
              {/*
                ⚠️ TWO BARS, AND NEITHER IS SUBTRACTED FROM THE OTHER. The
                tempting third band — "played before, but not this week" — is
                `childrenWithAnyEvents − activeThisWeek`, and those two numbers
                come from different tables: one counts sessions started inside
                the window, the other counts children with a recorded event ever.
                A session that ran unrecorded (I5) makes the first without the
                second, so that subtraction goes negative on real data. Two
                honest bars beat three that quietly clamp.
              */}
              <div className="lg:col-span-7 rounded-3xl border border-line bg-surface p-4 shadow-card sm:p-6">
                <h2 className="text-dash-md font-extrabold text-ink">{t.whoHasPlayed}</h2>
                <p className="mt-0.5 text-dash-2xs font-bold text-muted">
                  {t.whoHasPlayedHint(o.windowDays)}
                </p>
                <div className="mt-5">
                  {/* ⚠️ SHORT LABELS, BECAUSE THE KIT PRINTS THE FRACTION ITSELF.
                      The sentence forms — `playedThisWeek`, `touchedSkills` —
                      already carry "4 of 33" inside them, and beside a bar that
                      also prints it the row said the same numbers twice. */}
                  <ShareBars
                    emptyMessage={t.chartNoChildren}
                    rows={[
                      {
                        key: "played",
                        label: t.chartPlayedRow(o.windowDays),
                        count: o.activeThisWeek,
                        total: o.childCount,
                        tone: "primary",
                      },
                      {
                        key: "ever",
                        label: t.chartEverRow,
                        count: o.childrenWithAnyEvents,
                        total: o.childCount,
                        tone: "teal",
                      },
                    ]}
                  />
                </div>
              </div>

              {/*
                🔴 THE CLASSES THE ROSTER BELOW IS ALREADY HOLDING, SORTED THE
                OTHER WAY. "Top Performing Classes" named UKG-A, LKG-B, UKG-B and
                LKG-A — four classes that exist in no school — and called them
                performing without ever saying at what. These four have the
                largest share of their own children playing, which is the only
                ranking this payload supports, and they come from the same array
                as the table below, so the two cannot disagree.
              */}
              <div className="lg:col-span-5 rounded-3xl border border-line bg-surface p-4 shadow-card sm:p-6">
                <h2 className="text-dash-md font-extrabold text-ink">{t.busiestClasses}</h2>
                <p className="mt-0.5 text-dash-2xs font-bold text-muted">
                  {t.busiestClassesHint(o.windowDays)}
                </p>
                <div className="mt-5">
                  {/*
                    🔴 `RankedBars`, NOT `ShareBars.slice(0, 4)` — AND THE ROWS
                    OPEN NOW. Four bars that named no cut-off read as the whole
                    school; this draws five, says how many it is holding back,
                    and each row is a button into that class's consent screen.
                    A ranking a reader cannot act on from is a poster.

                    ⚠️ `coral`, NOT `leaf`. The list is quietest-first now, so
                    the top row is the class that most needs looking at — a
                    green bar over it would be saying the opposite of the
                    finding.
                  */}
                  <RankedBars
                    rows={ranked.map((c) => ({
                      key: c.classId,
                      label: c.name,
                      sub: c.teacherName ?? t.reportsNoTeacher,
                      count: c.activeThisWeek,
                      total: c.childCount,
                      tone: "coral" as const,
                      onOpen: () => {
                        onOpenClassConsent(c.classId);
                      },
                    }))}
                    emptyMessage={t.noClassPlayedYet}
                    moreLabel={t.classesRankedMore}
                    fewerLabel={t.classesRankedFewer}
                  />
                </div>
              </div>
            </div>

            {/*
              ✅ THE QUICK ACTIONS BAR IS GONE, AND IT STRANDED NOTHING.

              It had already shrunk from six buttons to three: two of the
              original six named features this product does not have ("Assign
              Game", "Create Assignment" — no assignment in the schema, no
              endpoint, no screen), and a fourth, "Broadcast", went with the
              school notice board it opened. What was left — "Add a class", "Add
              a teacher", "Add a child" — was three shortcuts to three sidebar
              items, on a page whose stat strip ALREADY opens the same three
              screens.

              🔴 A SHORTCUT IS NOT AN ENTRANCE, WHICH IS WHY NOTHING NEEDS A NEW
              DOOR. `school-classes`, `school-teachers` and `school-students` are
              each a nav row in `routes.ts` — a reader loses a click, not a
              destination. That is the whole test for deleting a bar like this
              one, and it is the test the broadcast button FAILED: its screen was
              removed underneath it, so it had to go rather than be re-pointed.

              ⚠️ AND IT WAS THE SECOND CONTROL FOR AN ACT THIS PAGE ALREADY
              OFFERS. "Add a class" landed on E4, which is where the Classes tile
              lands; the bar's own note used to argue the two were different
              because one is a WRITE and the other a READ — but both are the same
              `onNavigate("classes")`, and a reader who arrives with either
              intent gets the same screen with the same add form on it.
            */}

            {/* 6. Exception Notes List */}
            <div className="mt-4 flex flex-col gap-3">
              {schoolNotes(o).map((note) => (
                <Note
                  key={note}
                  tone={note === "CHILDREN_WITH_NO_CLASS" ? "warning" : "neutral"}
                  icon={note === "CHILDREN_WITH_NO_CLASS" ? <AlertIcon size={16} /> : <InfoIcon size={16} />}
                >
                  {noteText(note, lang, o.childrenWithNoClass)}
                </Note>
              ))}
            </div>

            {/*
              7. Games Usage Section — AND THE ONE BLOCK ON THIS PAGE THE DATE
              FILTER DOES NOT REACH, WHICH IS WHY IT NOW SAYS SO.

              🔴 THE DATA LAYER HAS NO WINDOW TO GIVE IT. `SkillsService.
              breakdownFor` counts every event ever recorded and takes no dates
              at all — the weekly digest's own note says the same thing about it
              — so these figures were always all-time. What changed is that
              everything AROUND them now moves with the filter, and an all-time
              number sitting silently in a filtered page reads as a filtered
              number that disagrees with its neighbours.

              🔴 IT IS LABELLED RATHER THAN WINDOWED, AND THAT IS A CORRECTNESS
              CALL, NOT A COST ONE. "Highest level reached" inside a date range
              would show Level 2 for a child who finished Level 5 last term.
              A report card that under-sells a child to their school is worse
              than one that says which period it covers.

              ⚠️ SAME PRECEDENT AS THE "HAVE EVER PLAYED" BAR BELOW THE CHART,
              which has always been all-time beside a windowed one and says so
              in its own label.
            */}
            <SectionHead
              count={o.skills.length}
              hint={`${t.gamesHint} ${t.gamesAllTime}`}
            >
              {t.gamesTitle}
            </SectionHead>
            {/*
              ⚠️ THE COLUMN COUNTS ARE CHOSEN AROUND FIVE GAMES, WHICH IS WHAT
              `GAME_FOR_SKILL` HOLDS. Five is why there is no `grid-cols-4` step
              here: it would strand a single card alone on a second row at the
              widest sizes, which reads as a game that failed to load rather than
              as the end of the list. Three (3 + 2) and five (one row) are the
              two shapes five tiles make without an orphan.

              ⚠️ THREE-UP NOW STARTS AT `lg` RATHER THAN `xl`. A 1180px window
              was getting two cards per row, each with a plate two thirds of its
              own width tall — the whole section cost more than a screen for five
              figures. The same window now holds three.
            */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-5">
              {o.skills.map((row, i) => (
                <GameUsageCard
                  key={row.skill}
                  row={row}
                  childCount={o.childCount}
                  lang={lang}
                  index={i}
                  onOpen={onOpenGame}
                />
              ))}
            </div>

            {/*
              ✅ 8. THE SCHOOL CLASSES ROSTER IS GONE, AND IT WAS THE THIRD
              CLASS LIST A HEAD TEACHER WAS BEING SHOWN.

              It drew Class · Band · Teacher · Children · Played · Year ·
              Consent, sorted quietest-first, paginated seven at a time. Every
              part of that already existed somewhere a reader was more likely to
              look:

                · FOUR OF ITS SEVEN COLUMNS ARE E4's. `ClassesScreen` lists
                  every class with its band, its teacher and its roll, plus the
                  search, the status, the add form and the expand-to-manage
                  panel this table had no version of. A dashboard that reprints
                  a nav item's table teaches the reader that the nav item is
                  optional, and then disagrees with it the first time one read
                  lands a second after the other.

                · ITS ONE DISTINCTIVE JOB WAS ALREADY DONE TWENTY LINES ABOVE.
                  "Classes that need a look" is the same array, sorted the same
                  way, and every bar in it ALREADY opens that class's consent —
                  see `RankedBars`' `onOpen`. Two controls for one action, on
                  one screen, is the split this folder keeps having to undo; the
                  `ShareBars` note at the top of this file is the last time.

                · `Year` WAS EMPTY IN PRACTICE. `academicYear` is null on every
                  class until somebody sets it, so the column drew a dash down
                  its whole length, and the Consent column's header was blank.

              ⚠️ ARCHIVED CLASSES ARE THE ONE THING ONLY THIS TABLE SHOWED —
              `ranked` filters them out on purpose ("they are not quiet, they
              are closed"). They are not lost: E4 lists them with a Status
              column, which is a better home for a closed class than a
              dashboard whose every other figure is about this week.
            */}
          </>
        )}
      </Surface>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────── E5 ── */

/*
 * ✅ `SchoolReportsProps` NOW LIVES WITH THE SCREEN IT DESCRIBES.
 * E5 moved to `SchoolReportsScreen.tsx`, which declares its own props and is
 * re-exported from line 2 of this file. The local `= Base` alias that used to
 * sit here conflicted with that re-export (TS2484) and described a component
 * this file no longer contains.
 */

/**
 * ⚠️ COVERAGE, NOT A REPORT CARD, AND NOT A PDF — OD-59. The Holistic Progress
 * Card is a specific Government of India artefact and no document in this repo
 * defines its fields; a guessed layout is a document a school might file. The
 * screen says so in words rather than leaving the omission to be discovered.
 */
